import React, { Dispatch, useEffect, useState, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import axios from 'axios'
import { useNavigate } from 'react-router-dom'

import { RootState } from '../../store'
import SuccessBody from './SuccessBody'
import LoadingAnimation from '../shared/LoadingAnimation'
import LoadingFailed from '../shared/LoadingFailed'
import StageNavigation from '../shared/StageNavigation'
import * as stages from '../../routes'
import { IParticipant } from '../../store/participant/interfaces'
import { IRegistration } from '../../store/registrations/interfaces'
import { setSuccessfulResponse } from '../../store/misc/misc'
import { setParticipant } from '../../store/participant/participant'
import { setPaymentMethod } from '../../store/paymentData'
import { removeAllRegistrations } from '../../store/registrations/registrations'
import { getNewRegistrations } from '../../store/registrations/selectors'
import { deleteLastVisitedRoute } from '../../store/localStorage'
import successBody from './SuccessBody'
import * as routes from '../../routes'

export enum RegistrationStatus {
  YES = '1_YES',
  MAYBE = '2_MAYBE',
  NO = '3_NO',
  RECEIVED = '4_RECEIVED',
}

export type PassData = {
  name: string;
  url: string;
}

export type VerifyResponse = {
  message: string
  message_plain: string
  submitted: boolean
  info: {
    rid?: number
    registration_states?: {
      [key in RegistrationStatus]: string[]
    }
    uuid?: string
  }
  pass_url: string
}

// The following use of any may seem weird, but redux-toolkit
// actually types the dispatch function as Dispatch<any> when
// you use the useDispatch hook.
export const resetRegistration = (dispatch: Dispatch<any>) => {
  dispatch(setParticipant({} as IParticipant))
  dispatch(setPaymentMethod(''))
  dispatch(removeAllRegistrations())
}

/**
 * Transform given parts of the state into a request body
 * the backend actually can understand.
 *
 * @param participant participant data to use
 * @param registrations new registrations to submit (existing ones stay untouched on the server)
 * @param paymentData payment data to use
 */
const buildRequestBody = (
  participant: IParticipant,
  registrations: IRegistration[],
  paymentData: RootState['paymentData'],
) => ({
  participant: {
    ...participant,
    gender: participant.gender || "d",
    children: participant.children?.map(child => ({
      ...child,
    })),
    custom_questions: participant.custom_answers?.map(cq => ({
      id: cq.question_id,
      answer: cq.answer_text,
    })),
  },
  registrations: registrations.map(reg => ({
    event_id: reg.event.event_id,
    order: reg.priority, // do not send as "priority" so server auto-assigns them
    number_of_adults: reg.numberOfAdults,
    number_of_children: reg.numberOfChildren,
    group_code: reg.groupCode,
    special_notes: reg.specialNotes,
    price_category:
      reg.event.price_categories.length > 0
        ? reg.event.price_categories.find(cat => cat.name === reg.priceCategory)
          ?.id
        : undefined,
    custom_questions: reg.customAnswers.map(cq => ({
      id: cq.id,
      answer: cq.answer,
    })),
    discount_codes: paymentData.discountCodes.map((c) => ({...c, code: c.used_code})),
  })).sort((a, b) => a.order - b.order),
  payment: {
    method: paymentData.method,
    ...paymentData.debitData,
    payer_city: `${paymentData.debitData.payer_zip} ${paymentData.debitData.payer_city}`,
    holder_city: `${paymentData.debitData.holder_zip} ${paymentData.debitData.holder_city}`
  },
})

/**
 * Component containing the entire success stage.
 *
 * This will send a registration request to the backend when mounted,
 * and provides retry functionality on failure.
 */
const SuccessStage = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const [requestFailedReason, setRequestFailedReason] = useState("")
  const [requestLoading, setRequestLoading] = useState(false)
  const [retryCountdown, setRetryCountdown] = useState(0)
  const hasSubmitted = useRef(false)

  const { baseUrl, programId, successfulResponse } = useSelector(
    (state: RootState) => state.misc,
  )
  const { data: participant } = useSelector(
    (state: RootState) => state.participant,
  )
  const newRegistrations = useSelector(getNewRegistrations)
  const paymentData = useSelector((state: RootState) => state.paymentData)

  const submitRegistration = async () => {
    if (hasSubmitted.current || successfulResponse?.submitted) {
      console.log('Registration already submitted, skipping...')
      return
    }

    hasSubmitted.current = true
    console.log('Sending registration request to server...')
    const reqData = buildRequestBody(participant, newRegistrations, paymentData)
    console.debug('< sending request', reqData)

    const cookie = document.cookie.match(
      '(^|;)\\s*csrftoken\\s*=\\s*([^;]+)',
    )
    const csrftoken = cookie ? cookie[2] : ''

    setRequestLoading(true)
    setRequestFailedReason("") // Clear any previous errors

    try {
      const resp = await axios.post(
        `${baseUrl}/api/programs/${programId}/participant-registration`,
        reqData,
        { headers: { 'X-CSRFToken': csrftoken } },
      )

        console.debug('> got response from server:')
        console.debug(resp.data)
        dispatch(setSuccessfulResponse(resp.data))

        if (resp.data?.submitted) {
          resetRegistration(dispatch)
          deleteLastVisitedRoute()
        }
    } catch (error: any) {
      console.warn("Registration did not succeed: Error is", error.message)
      setRequestFailedReason(error.message)
      hasSubmitted.current = false // Reset on error to allow retry
      setRetryCountdown(5) // Start 5-second countdown on error
    } finally {
      setRequestLoading(false)
    }
  }

  // Submit registration on component mount
  useEffect(() => {
    submitRegistration()
  }, []) // Empty dependency array - only run once on mount

  // Handle countdown timer
  useEffect(() => {
    if (retryCountdown > 0) {
      const timer = setTimeout(() => {
        setRetryCountdown(retryCountdown - 1)
      }, 1000)
      return () => clearTimeout(timer)
    }
  }, [retryCountdown])

  return (
    <>
      {requestLoading && (
        <LoadingAnimation text="Die Anmeldung wird übermittelt…"/>
      )}

      {requestFailedReason && (
          <LoadingFailed text="Fehler: Die Anmeldung konnte nicht übermittelt werden." additionalInfo={"Fehlermeldung: " + requestFailedReason}>

            <button
              className="btn btn-primary"
              onClick={submitRegistration}
              disabled={requestLoading || retryCountdown > 0}
            >
              {retryCountdown > 0 ? `Erneut versuchen (${retryCountdown}s)` : "Erneut versuchen"}
            </button>

          </LoadingFailed>
      )}

      {!requestLoading && !requestFailedReason && successfulResponse?.submitted !== undefined && (
        <SuccessBody resp={successfulResponse} />
      )}
    </>

  )
}

export default SuccessStage
